import type {
    DecoratedPerformanceReview,
    PerformanceMomentCta,
    PerformanceReviewConversationListItem,
    PerformanceReviewRelationship,
    PerformanceReviewResponse,
} from "~vue/types/performance_management";

/** Join reviews with their CTA, relationship and conversation records. Shared by the PM page and the in-chat picker. */
export const decorateReviews = (
    reviews: PerformanceReviewResponse[],
    ctas: PerformanceMomentCta[],
    relationships: PerformanceReviewRelationship[],
    conversations: PerformanceReviewConversationListItem[],
): DecoratedPerformanceReview[] =>
    reviews.map((review) => ({
        ...review,
        cta: ctas.find((c) => c.id === review.performance_moment_cta_id) || null,
        relationship: review.performance_review_relationship_id === null ? null : relationships.find((r) => r.id === review.performance_review_relationship_id) || null,
        conversation: conversations.find((c) => c.performance_review_id === review.id) || null,
    }));

export const STATUS = {
    NOT_STARTED: "Not Started",
    IN_PROGRESS: "In progress",
    COMPLETE: "Complete",
} as const;

export const isReviewCompleted = (review: DecoratedPerformanceReview): boolean => {
    return !!review.manually_completed_at || review.conversation?.state === "completed";
};

const isReviewInProgress = (review: DecoratedPerformanceReview): boolean => !isReviewCompleted(review) && review.conversation?.state === "in_progress";

export const getReviewGroupStatus = (reviews: DecoratedPerformanceReview[]): string => {
    const completed = reviews.filter(isReviewCompleted).length;
    const total = reviews.length;
    if (completed === 0) {
        return reviews.some(isReviewInProgress) ? STATUS.IN_PROGRESS : STATUS.NOT_STARTED;
    }
    if (completed === total) {
        return STATUS.COMPLETE;
    }
    return `${completed} of ${total} done`;
};

export const getReviewGroupStatusColor = (reviews: DecoratedPerformanceReview[]): string => {
    const completed = reviews.filter(isReviewCompleted).length;
    if (completed === 0 && !reviews.some(isReviewInProgress)) {
        return "bg-[#ECECEC] text-valence-grey-750";
    }
    if (completed === reviews.length) {
        return "bg-valence-green-olive-light text-valence-green-olive";
    }
    return "bg-valence-primary-200 text-valence-grey-750";
};
